import React, { useState, useEffect } from "react";
import {
  Button,
  DialogTitle,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Switch,
  Box,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import EditIcon from "@mui/icons-material/Edit";
import { grey, orange } from "@mui/material/colors";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../../components/loader/Loader";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import {
  showModal,
  hideConfirmModal,
  showConfirmModal,
  storeShapeList,
} from "../../redux/counterSlice";
import { useTablePagination, useTableHeight } from "../../hooks/useTablePagination";
import { useForm } from "react-hook-form";
import { PhotoProvider, PhotoView } from "react-photo-view";
import AddNewShape from "./AddNewShape";

const CustomDesign = () => {
  const dispatch = useDispatch();
  const methods = useForm();
  const { reset } = methods;
  const shapeList = useSelector((state) => state.counter.shapeList || []);
  const [loader, setLoader] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openNewShapeDialog, setOpenNewShapeDialog] = useState(false);

  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("shape", 10);
  const tableHeight = useTableHeight();

  // ✅ Clear search on mount to remove the comma
  useEffect(() => {
    if (searchText === "," || searchText === null || searchText === undefined) {
      onSearchChange("");
    }
  }, []);

  const loadInitialData = async () => {
    try {
      setLoader(true);
      const response = await axios.get("/shape");

      const data = response.data?.data || [];
      dispatch(storeShapeList(data));
    } catch (error) {
      toast.error(error?.message || "Failed to load shape data");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [page, rowsPerPage]);

  const toggleStatus = async (item) => {
    const updatedStatus = item.active_status === 1 ? 0 : 1;

    try {
      setLoader(true);
      await axios.put(`/shape/${item.shape_id}`, {
        shape_name: item.shape_name,
        aspect_ratio: item.aspect_ratio,
        active_status: updatedStatus,
      });

      toast.success(
        updatedStatus === 1 ? "Shape Activated" : "Shape Deactivated"
      );
      loadInitialData();
    } catch (err) {
      console.error(
        "Error while updating status:",
        err.response?.data || err.message
      );
      toast.error(err?.response?.data?.message || "Failed to update status");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal());
    }
  };

  // ✅ Safely filter with fallback for searchText
  const safeSearchText = searchText || "";
  const filteredShapeList = shapeList.filter((shape) =>
    shape.shape_name
      ?.toString()
      .toLowerCase()
      .includes(safeSearchText.toLowerCase())
  );

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{+dataIndex + 1}</p>;
        },
        filter: false,
      },
    },
    {
      name: "shape_name",
      label: "Shape Name",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "aspect_ratio",
      label: "Ratio",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const item = filteredShapeList[dataIndex];
          const status = item?.active_status;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1}
              onChange={() => {
                const confirmLabel = status === 1 ? "Deactivate" : "Activate";
                dispatch(
                  showConfirmModal({
                    msg: confirmLabel,
                    action: () => toggleStatus(item),
                  })
                );
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
        filter: true,
        filterOptions: {
          names: ["Active", "Inactive"],
          logic(value, filters) {
            if (filters.length) {
              if (filters.includes("Active") && +value === 1) return false;
              if (filters.includes("Inactive") && +value === 0) return false;
              return true;
            }
            return false;
          },
        },
      },
    },
    {
      name: "Actions",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = filteredShapeList[dataIndex];
          const disabled = row?.active_status === 1;
          return (
            <div className="actionBtns">
              <Button
                disabled={disabled}
                onClick={() => {
                  setSelectedRow(row);
                  setOpenNewShapeDialog(true);
                }}
              >
                <EditIcon
                  sx={{ color: disabled ? grey[600] : "secondary.main" }}
                />
              </Button>
            </div>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    print: false,
    viewColumns: true,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: safeSearchText, 
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No matching records found",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Shapes</h4>
          <MUIDataTable
            data={filteredShapeList}
            columns={columns}
            options={options}
            title={
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setSelectedRow(null);
                  setOpenNewShapeDialog(true);
                }}
                startIcon={<AddIcon />}
              >
                New Shape
              </Button>
            }
          />
          <AddNewShape
            open={openNewShapeDialog}
            handleClose={() => {
              setOpenNewShapeDialog(false);
              setSelectedRow(null);
            }}
            selectedRow={selectedRow}
            loadInitialData={loadInitialData}
          />
        </>
      )}
    </>
  );
};

export default CustomDesign;